import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { EmptyState, ErrorState, LoadingSkeleton, PageHeader, StatusBadge } from '../components/ui';
import { ApiError, Business, businessApi } from '../lib/api';

interface BusinessCardProps {
  business: Business;
  onNavigateDashboard: (id: number) => void;
  onEditOnboarding: (id: number) => void;
}

function initialsFor(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

function BusinessCard({ business, onNavigateDashboard, onEditOnboarding }: BusinessCardProps) {
  const primaryLocation = business.locations?.[0];
  const isOnboardingComplete = business.onboarding?.completed ?? false;
  const serviceCount = business.services?.length ?? 0;
  const targetCount = business.targetLocations?.length ?? 0;
  const cleanWebsite = business.website?.replace(/^https?:\/\//, '');

  return (
    <article className="business-card premium-business-card" onClick={() => onNavigateDashboard(business.id)}>
      <div className="premium-business-topline">
        <div className="business-card-avatar">{initialsFor(business.name)}</div>
        <div className="business-card-meta">
          <div className="business-card-name">{business.name}</div>
          <div className="business-card-industry">{business.industry ?? 'General Business'}</div>
        </div>
        <StatusBadge tone={isOnboardingComplete ? 'success' : 'warning'}>
          {isOnboardingComplete ? 'Ready' : 'In progress'}
        </StatusBadge>
      </div>

      <div className="premium-business-details">
        {business.website && (
          <a
            href={business.website.startsWith('http') ? business.website : `https://${business.website}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => event.stopPropagation()}
          >
            {cleanWebsite}
          </a>
        )}
        {primaryLocation && (
          <span>
            {[primaryLocation.city, primaryLocation.state, primaryLocation.country].filter(Boolean).join(', ')}
          </span>
        )}
      </div>

      {business.description && <p className="premium-business-description">{business.description}</p>}

      <div className="premium-business-stats">
        <div>
          <strong>{serviceCount}</strong>
          <span>Services</span>
        </div>
        <div>
          <strong>{targetCount}</strong>
          <span>Target cities</span>
        </div>
        <div>
          <strong>{business.isActive ? 'Live' : 'Off'}</strong>
          <span>Status</span>
        </div>
      </div>

      <div className="business-card-actions" onClick={(event) => event.stopPropagation()}>
        <button
          id={`open-dashboard-${business.id}`}
          className="btn btn-primary btn-sm"
          onClick={() => onNavigateDashboard(business.id)}
          type="button"
        >
          SEO Dashboard
        </button>
        <button
          id={`edit-onboarding-${business.id}`}
          className="btn btn-secondary btn-sm"
          onClick={() => onEditOnboarding(business.id)}
          type="button"
        >
          Settings
        </button>
      </div>
    </article>
  );
}

export default function BusinessesPage() {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  async function fetchBusinesses() {
    try {
      setLoading(true);
      setError('');
      setBusinesses(await businessApi.list());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'We could not load your businesses.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void fetchBusinesses();
  }, []);

  return (
    <DashboardLayout
      title="Businesses"
      subtitle="Manage your local businesses and access their SEO project dashboards"
      breadcrumbs={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'Businesses' }]}
    >
      <PageHeader
        eyebrow="Portfolio"
        title="Managed Businesses"
        description="Each location has its own SEO command center, Google data, ranking intelligence, and automation history."
        actions={
          <button id="onboard-business-header-btn" className="btn btn-primary" onClick={() => navigate('/businesses/new')} type="button">
            Onboard New Business
          </button>
        }
      />

      {error && <ErrorState message={error} onRetry={() => void fetchBusinesses()} />}

      {loading ? (
        <LoadingSkeleton rows={5} />
      ) : businesses.length === 0 ? (
        <EmptyState
          title="No businesses yet"
          description="Complete the onboarding wizard for your first local business to unlock audits, keyword data, and AI recommendations."
          action={
            <button className="btn btn-primary" onClick={() => navigate('/businesses/new')} type="button">
              Start Onboarding Wizard
            </button>
          }
        />
      ) : (
        <div className="business-grid premium-business-grid">
          {businesses.map((business) => (
            <BusinessCard
              key={business.id}
              business={business}
              onNavigateDashboard={(id) => navigate(`/businesses/${id}/dashboard`)}
              onEditOnboarding={(id) => navigate(`/businesses/${id}/onboarding`)}
            />
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
